import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { clsx } from 'clsx';
import { isTamilAnswerAccepted } from '../../utils/tamilAnswerNormalize';
import { readWorkbookJson, writeWorkbookJson, type WorkbookActivityCompletion } from './workbookPersist';

export type QAQuestion = {
  q_no: number;
  text: string;
  accepted_answers?: string[];
  hint?: string;
  explanation?: string;
};

type Supplementary = { heading?: string; lines?: string[] };

export interface QACardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  questions: QAQuestion[];
  supplementary?: Supplementary;
  sourcePage?: number;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type ValidationState = 'correct' | 'incorrect' | 'teacher_review';

type PersistShape = {
  answers: Record<number, string>;
  qIndex: number;
  validation?: Record<number, ValidationState>;
};

function hasAnswerKey(q: QAQuestion): boolean {
  return Array.isArray(q.accepted_answers) && q.accepted_answers.length > 0;
}

export const QACard: React.FC<QACardProps> = ({
  workbookLabel,
  title,
  instructions,
  questions,
  supplementary,
  sourcePage,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-qa-${persistenceId}` : '';

  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const questionsStableKey = questions
    .map((q) => q.q_no)
    .sort((a, b) => a - b)
    .join('|');

  const initialAnswers = useMemo(() => {
    const m: Record<number, string> = {};
    for (const q of questions) m[q.q_no] = '';
    return m;
  }, [questions]);

  const [answers, setAnswers] = useState<Record<number, string>>(initialAnswers);
  const [qIndex, setQIndex] = useState(0);
  const [hintOpen, setHintOpen] = useState(false);
  const [validation, setValidation] = useState<Record<number, ValidationState>>({});

  useEffect(() => {
    const list = questionsRef.current;
    const empty: Record<number, string> = {};
    for (const q of list) empty[q.q_no] = '';

    if (!storageKey) {
      setAnswers(empty);
      setQIndex(0);
      setValidation({});
      return;
    }
    const loaded = readWorkbookJson<PersistShape | null>(storageKey, null);
    if (loaded?.answers && typeof loaded.answers === 'object') {
      const merged = { ...empty };
      const ra = loaded.answers as Record<string, unknown>;
      for (const k of Object.keys(ra)) {
        const q = Number(k);
        if (!Number.isFinite(q)) continue;
        merged[q] = String(ra[k] ?? '');
      }
      setAnswers(merged);
    } else {
      setAnswers(empty);
    }
    const max = Math.max(0, list.length - 1);
    const qi = typeof loaded?.qIndex === 'number' ? Math.min(Math.max(0, loaded.qIndex), max) : 0;
    setQIndex(qi);
    const v: Record<number, ValidationState> = {};
    if (loaded?.validation && typeof loaded.validation === 'object') {
      const rv = loaded.validation as Record<string, unknown>;
      for (const k of Object.keys(rv)) {
        const qn = Number(k);
        if (!Number.isFinite(qn)) continue;
        const val = rv[k];
        if (val === 'correct' || val === 'incorrect' || val === 'teacher_review') v[qn] = val;
      }
    }
    setValidation(v);
  }, [storageKey, questionsStableKey, questions.length]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { answers, qIndex, validation } satisfies PersistShape);
  }, [storageKey, answers, qIndex, validation]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const keyedQuestions = useMemo(() => questions.filter(hasAnswerKey), [questions]);
  const keyedTotal = keyedQuestions.length;
  const keyedCorrectCount = useMemo(
    () => keyedQuestions.filter((q) => validation[q.q_no] === 'correct').length,
    [keyedQuestions, validation]
  );
  const teacherReviewCount = useMemo(
    () => questions.filter((q) => !hasAnswerKey(q) && validation[q.q_no] === 'teacher_review').length,
    [questions, validation]
  );

  useEffect(() => {
    if (!onWorkbookStatusChange || questions.length === 0) return;
    const allText = questions.every((q) => (answers[q.q_no] ?? '').trim().length > 0);
    if (!allText) {
      const any = questions.some((q) => (answers[q.q_no] ?? '').trim().length > 0);
      onWorkbookStatusChange(any ? 'in_progress' : 'not_started');
      return;
    }
    if (keyedTotal === 0) {
      onWorkbookStatusChange('completed');
      return;
    }
    const allKeyedCorrect = keyedQuestions.every((q) => validation[q.q_no] === 'correct');
    onWorkbookStatusChange(allKeyedCorrect ? 'completed' : 'in_progress');
  }, [onWorkbookStatusChange, questions, answers, validation, keyedQuestions, keyedTotal]);

  const total = questions.length;
  const safeIdx = Math.min(Math.max(0, qIndex), Math.max(0, total - 1));
  const q = questions[safeIdx];

  const setAnswerFor = (qNo: number, value: string) => {
    mark();
    setAnswers((prev) => ({ ...prev, [qNo]: value }));
    setValidation((prev) => {
      if (!(qNo in prev)) return prev;
      const next = { ...prev };
      delete next[qNo];
      return next;
    });
  };

  const checkCurrentAnswer = () => {
    mark();
    const raw = (answers[q.q_no] ?? '').trim();
    if (!raw) return;

    if (!hasAnswerKey(q)) {
      setValidation((prev) => ({ ...prev, [q.q_no]: 'teacher_review' }));
      return;
    }

    const ok = isTamilAnswerAccepted(raw, q.accepted_answers!);
    setValidation((prev) => ({ ...prev, [q.q_no]: ok ? 'correct' : 'incorrect' }));
  };

  const dotClass = (qi: number) => {
    const qq = questions[qi];
    const hasText = !!answers[qq.q_no]?.trim();
    const v = validation[qq.q_no];
    const keyed = hasAnswerKey(qq);
    if (qi === safeIdx) {
      return 'scale-125 border-amber-600 bg-amber-500 shadow-md dark:border-amber-400';
    }
    if (v === 'correct' || v === 'teacher_review') {
      return 'border-emerald-500 bg-emerald-400 dark:bg-emerald-600';
    }
    if (v === 'incorrect') {
      return 'border-rose-500 bg-rose-200 dark:border-rose-400 dark:bg-rose-800';
    }
    if (hasText && keyed) {
      return 'border-sky-500 bg-sky-200 dark:border-sky-400 dark:bg-sky-800';
    }
    if (hasText) {
      return 'border-emerald-400 bg-emerald-300 dark:bg-emerald-600';
    }
    return 'border-amber-300 bg-white dark:border-amber-700 dark:bg-slate-700';
  };

  if (!q || total === 0) {
    return (
      <div className="rounded-[2rem] border-4 border-amber-200 bg-amber-50 p-8 text-center text-lg tamil-text dark:border-amber-800 dark:bg-amber-950/40" lang="ta">
        வினாக்கள் இல்லை
      </div>
    );
  }

  const currentValidation = validation[q.q_no];
  const showTeacherLine = currentValidation === 'teacher_review';
  const showCorrectLine = currentValidation === 'correct';
  const showIncorrectLine = currentValidation === 'incorrect';
  const showHintAfterWrong = showIncorrectLine && q.hint?.trim();

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-amber-300/90 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 shadow-[0_20px_50px_-12px_rgba(245,158,11,0.4)] dark:border-amber-700/60 dark:from-amber-950/40 dark:via-orange-950/30 dark:to-slate-900">
      <div className="border-b border-amber-200/70 bg-white/55 px-5 py-4 backdrop-blur-sm dark:border-amber-800/40 dark:bg-slate-900/50">
        {workbookLabel ? (
          <p className="text-sm font-bold text-amber-800 tamil-text dark:text-amber-200" lang="ta">
            {workbookLabel}
          </p>
        ) : null}
        <h2 className="mt-1 text-2xl font-extrabold text-amber-950 tamil-text md:text-3xl dark:text-amber-50" lang="ta">
          {title}
        </h2>
        {instructions ? (
          <p className="mt-2 text-lg leading-relaxed text-amber-900/95 tamil-text dark:text-amber-100/90" lang="ta">
            {instructions}
          </p>
        ) : null}
        {sourcePage != null ? (
          <span className="mt-3 inline-block rounded-full bg-amber-600 px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
        ) : null}
      </div>

      <div className="px-4 py-8 sm:px-8">
        {keyedTotal > 0 ? (
          <div className="mb-4 rounded-2xl border-4 border-white/90 bg-white/80 px-4 py-3 text-center text-sm font-bold text-amber-900 shadow-inner dark:border-slate-600 dark:bg-slate-800/80 dark:text-amber-100 tamil-text" lang="ta">
            சுய மதிப்பீடு (பதில்குறிப்பு உள்ள வினாக்கள்): {keyedCorrectCount} / {keyedTotal} சரி
            {teacherReviewCount > 0 ? (
              <span className="mt-1 block text-xs font-semibold text-app-primary dark:text-violet-200">
                ஆசிரியர் பரிசீலனை: {teacherReviewCount} வினா(கள்) குறிக்கப்பட்டுள்ளன
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="mb-6 flex items-center justify-center gap-2">
          {Array.from({ length: total }, (_, qi) => (
            <button
              key={qi}
              type="button"
              onClick={() => {
                mark();
                setQIndex(qi);
              }}
              className={clsx(
                'h-4 w-4 rounded-full border-2 transition sm:h-5 sm:w-5',
                dotClass(qi)
              )}
              aria-label={`வினா ${qi + 1}`}
            />
          ))}
        </div>

        <div className="rounded-3xl border-4 border-white bg-white/90 p-6 shadow-inner dark:border-slate-600 dark:bg-slate-800/90">
          <p className="text-center text-sm font-bold text-amber-700 dark:text-amber-300 tamil-text" lang="ta">
            வினா {safeIdx + 1} / {total}
          </p>
          <p className="mt-6 text-center text-xl font-bold leading-relaxed text-slate-900 tamil-text md:text-2xl dark:text-white" lang="ta">
            {q.text}
          </p>
          <label className="mt-8 block text-center text-sm font-bold text-amber-800 tamil-text dark:text-amber-200" lang="ta">
            உங்கள் விடை
          </label>
          <textarea
            value={answers[q.q_no] ?? ''}
            onChange={(e) => {
              setAnswerFor(q.q_no, e.target.value);
            }}
            rows={6}
            className="mt-3 w-full min-h-[12rem] resize-y rounded-2xl border-4 border-amber-200 bg-amber-50/50 p-4 text-xl leading-relaxed text-slate-900 shadow-inner outline-none ring-0 transition focus:border-amber-500 focus:bg-white dark:border-amber-800 dark:bg-slate-900 dark:text-white dark:focus:border-amber-400 tamil-text"
            lang="ta"
            placeholder="இங்கே எழுதுங்கள்…"
          />

          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={checkCurrentAnswer}
              disabled={!(answers[q.q_no] ?? '').trim()}
              className="min-h-[52px] rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-3 text-lg font-extrabold text-white shadow-xl transition enabled:hover:brightness-110 enabled:active:scale-[0.99] disabled:opacity-40 tamil-text"
              lang="ta"
            >
              பதில் சரிபார்
            </button>
          </div>

          {showCorrectLine ? (
            <div className="mt-4 space-y-2">
              <p
                className="rounded-2xl border-4 border-emerald-400 bg-emerald-50 px-4 py-3 text-center text-xl font-extrabold text-emerald-900 tamil-text dark:border-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-50"
                lang="ta"
              >
                சரி!
              </p>
              {q.explanation?.trim() ? (
                <p className="rounded-2xl border-2 border-emerald-200 bg-white/90 px-4 py-3 text-base leading-relaxed text-emerald-950 tamil-text dark:border-emerald-800 dark:bg-slate-900/90 dark:text-emerald-100" lang="ta">
                  {q.explanation}
                </p>
              ) : null}
            </div>
          ) : null}

          {showIncorrectLine ? (
            <div className="mt-4 space-y-2">
              <p
                className="rounded-2xl border-4 border-amber-400 bg-amber-50 px-4 py-3 text-center text-xl font-extrabold text-amber-950 tamil-text dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-50"
                lang="ta"
              >
                மீண்டும் முயற்சி செய்
              </p>
              {showHintAfterWrong ? (
                <p className="rounded-2xl border-4 border-app-primary/20 bg-app-primary/10 px-4 py-3 text-base font-semibold leading-relaxed text-app-primary tamil-text dark:border-app-primary/30 dark:bg-app-primary/15/40 dark:text-violet-50" lang="ta">
                  <span className="font-extrabold">குறிப்பு: </span>
                  {q.hint}
                </p>
              ) : null}
            </div>
          ) : null}

          {showTeacherLine ? (
            <p
              className="mt-4 rounded-2xl border-4 border-sky-300 bg-sky-50 px-4 py-3 text-center text-lg font-bold text-sky-950 tamil-text dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-50"
              lang="ta"
            >
              இந்த விடை ஆசிரியர் பரிசீலனைக்கு
            </p>
          ) : null}
        </div>

        {supplementary?.lines?.length ? (
          <div className="mt-6">
            <button
              type="button"
              onClick={() => setHintOpen((o) => !o)}
              className="flex w-full min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-app-primary px-4 py-3 text-base font-bold text-white shadow-lg transition hover:brightness-110 tamil-text"
              lang="ta"
            >
              <Lightbulb className="h-6 w-6 shrink-0" aria-hidden />
              {hintOpen ? 'குறிப்பை மறை' : 'குறிப்பைப் பார்'}
            </button>
            {hintOpen ? (
              <div className="mt-3 rounded-2xl border-4 border-app-primary/20 bg-app-primary/10 p-4 text-lg leading-relaxed text-app-primary tamil-text dark:border-app-primary/30 dark:bg-app-primary/15/40 dark:text-violet-50" lang="ta">
                {supplementary.heading ? <p className="mb-2 font-extrabold">{supplementary.heading}</p> : null}
                <ul className="space-y-2">
                  {supplementary.lines.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            disabled={safeIdx <= 0}
            onClick={() => {
              mark();
              setQIndex((i) => Math.max(0, i - 1));
            }}
            className="inline-flex min-h-[52px] min-w-[52px] items-center justify-center gap-2 rounded-2xl border-4 border-amber-300 bg-white px-5 py-3 text-base font-bold text-amber-900 shadow-md transition enabled:hover:bg-amber-50 disabled:opacity-40 dark:border-amber-700 dark:bg-slate-800 dark:text-amber-100 dark:enabled:hover:bg-slate-700 tamil-text"
            lang="ta"
          >
            <ChevronLeft className="h-6 w-6" aria-hidden />
            முந்தைய வினா
          </button>
          <button
            type="button"
            disabled={safeIdx >= total - 1}
            onClick={() => {
              mark();
              setQIndex((i) => Math.min(total - 1, i + 1));
            }}
            className="inline-flex min-h-[52px] min-w-[52px] items-center justify-center gap-2 rounded-2xl border-4 border-amber-300 bg-white px-5 py-3 text-base font-bold text-amber-900 shadow-md transition enabled:hover:bg-amber-50 disabled:opacity-40 dark:border-amber-700 dark:bg-slate-800 dark:text-amber-100 dark:enabled:hover:bg-slate-700 tamil-text"
            lang="ta"
          >
            அடுத்த வினா
            <ChevronRight className="h-6 w-6" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
};
